;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { PaginationMeta } from '@workspace/ui/types';
import { EMPLOYEE_PAYROLL_TYPES } from './constants';
import { normalizeDate } from './date';
import { parseDateString } from './format';

export const EXPENSE_TITLE_MAX_LENGTH = 100;
export const EXPENSE_DESCRIPTION_MAX_LENGTH = 1000;

/** MIME types allowed for expense receipt / attachment uploads. */
export const EXPENSE_ATTACHMENT_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;

export const EXPENSE_ATTACHMENT_INVALID_TYPE_MESSAGE =
  'Files must be JPG, JPEG, PNG, PDF, DOC, or DOCX';

export const EXPENSE_ATTACHMENT_FORMATS_LABEL = 'Accepted formats: JPG, JPEG, PNG, PDF, DOC, DOCX';

/**
 * Comma-separated list for `FileInput` `allowedDocumentTypes` and file validation.
 * Prefer `EXPENSE_ATTACHMENT_FORMATS_LABEL` for user-visible copy (avoids inferring labels from MIME strings).
 */
export const EXPENSE_ATTACHMENT_DOCUMENT_TYPES =
  '.jpg,.jpeg,.png,.pdf,.doc,.docx,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export function isExpenseAttachmentFileAccepted(
  file: File,
  allowedDocumentTypes: string = EXPENSE_ATTACHMENT_DOCUMENT_TYPES
): boolean {
  const allowedTypes = allowedDocumentTypes.split(',');
  if (allowedTypes.length === 0 || allowedTypes[0] === '*') return true;

  return allowedTypes.some((type) => {
    const normalizedType = type.trim();
    if (normalizedType.endsWith('/*')) {
      const category = normalizedType.split('/')[0];
      return file.type.startsWith(`${category}/`);
    }

    if (file.type === normalizedType) return true;
    if (
      normalizedType.startsWith('.') &&
      file.name.toLowerCase().endsWith(normalizedType.toLowerCase())
    ) {
      return true;
    }

    if (!normalizedType.startsWith('.') && !normalizedType.includes('/')) {
      return file.name.toLowerCase().endsWith(`.${normalizedType.toLowerCase()}`);
    }

    return false;
  });
}

export function isExpenseAttachmentMimeType(mimeType: string): boolean {
  return (EXPENSE_ATTACHMENT_MIME_TYPES as readonly string[]).includes(mimeType);
}

/** Non-expense monthly inputs that allow CSV/XLSX attachments (bonus, salary change, etc.). */
export const MONTHLY_INPUT_GENERAL_ATTACHMENT_DOCUMENT_TYPES =
  'csv,text/csv,xlsx,xls,pdf,application/pdf,jpg,jpeg,image/jpeg,png,image/png';

/** Leave / other monthly input attachments: images and PDF only (no CSV, no Word). */
export const MONTHLY_INPUT_LEAVE_RECEIPT_DOCUMENT_TYPES =
  '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf,jpg,jpeg,png,pdf';

export function getMonthlyInputFileUploadDocumentTypes(payrollType: string): string {
  if (payrollType === EMPLOYEE_PAYROLL_TYPES.EXPENSE) {
    return EXPENSE_ATTACHMENT_DOCUMENT_TYPES;
  }

  if (
    payrollType === EMPLOYEE_PAYROLL_TYPES.OTHER ||
    payrollType === EMPLOYEE_PAYROLL_TYPES.CONTRACT_AMENDMENT ||
    payrollType === EMPLOYEE_PAYROLL_TYPES.OVERTIME_HOURS ||
    payrollType === EMPLOYEE_PAYROLL_TYPES.UNPAID_LEAVE ||
    payrollType === EMPLOYEE_PAYROLL_TYPES.SICK_LEAVE
  ) {
    return MONTHLY_INPUT_LEAVE_RECEIPT_DOCUMENT_TYPES;
  }

  return MONTHLY_INPUT_GENERAL_ATTACHMENT_DOCUMENT_TYPES;
}

export function getMonthlyInputAcceptedFormatsLabel(payrollType: string): string | undefined {
  if (payrollType === EMPLOYEE_PAYROLL_TYPES.EXPENSE) {
    return EXPENSE_ATTACHMENT_FORMATS_LABEL;
  }

  return undefined;
}

export const EXPENSE_STATUSES = ['pending', 'approved', 'declined', 'paid', 'cancelled'] as const;

export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];

export const REIMBURSEMENT_METHODS = ['wallet', 'payroll', 'card'] as const;

export type ReimbursementMethod = (typeof REIMBURSEMENT_METHODS)[number];

export const EXPENSE_TYPES = ['prepaid_expense', 'reimbursement'] as const;

export type ExpenseType = (typeof EXPENSE_TYPES)[number];

export const PREPAID_RECEIPT_STATUSES = [
  'pending_receipt',
  'submitted',
  'receipt_approved',
  'receipt_declined',
] as const;

export type PrepaidReceiptStatus = (typeof PREPAID_RECEIPT_STATUSES)[number];

export function getExpenseStatusLabel(status: ExpenseStatus): string {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'declined':
      return 'Rejected';
    case 'pending':
      return 'Pending';
    case 'paid':
      return 'Paid';
    case 'cancelled':
      return 'Cancelled';
  }
}

export function sanitizeExpenseDownloadFilename(name: string): string {
  const trimmed = name.trim().slice(0, 200);
  const safe = trimmed.replace(/[/\\?%*:|"<>]/g, '_');
  return safe.length > 0 ? safe : 'download';
}
export interface ExpenseCategoryOption {
  name: string;
  value: string;
}

/** Default USD expense categories used when the categories API is unavailable (e.g. missing create permission). */
export const USD_EXPENSE_CATEGORIES: ExpenseCategoryOption[] = [
  { name: 'Travel expenses - fuel', value: 'travel_fuel' },
  { name: 'Travel expenses - hotels & flights', value: 'travel_hotels_flights' },
  { name: 'Travel expenses - transportation', value: 'travel_transportation' },
  { name: 'Meals', value: 'meals' },
  { name: 'Team building/entertainment', value: 'team_building_entertainment' },
  { name: 'Office supplies and equipment', value: 'office_supplies_equipment' },
  { name: 'Communication expenses', value: 'communication' },
  { name: 'Postage and courier expenses', value: 'postage_courier' },
  { name: 'Marketing and advertising', value: 'marketing_advertising' },
  { name: 'Conferences and events', value: 'conferences_events' },
  { name: 'IT costs/subscriptions', value: 'it_costs_subscriptions' },
  { name: 'Professional development', value: 'professional_development' },
  { name: 'Other', value: 'other' },
];

export function getExpenseCategoriesForDisplay(
  canFetchFromApi: boolean,
  fetchedCategories: ExpenseCategoryOption[]
): ExpenseCategoryOption[] {
  return canFetchFromApi ? fetchedCategories : USD_EXPENSE_CATEGORIES;
}

export interface Attachment {
  id: number;
  name: string;
  file_url: string;
}

export interface Expense {
  id: number;
  user_id: number;
  user_email: string;
  user_name: string;
  organisation_id: number;
  organisation_name?: string;
  currency_id: number;
  currency: string;
  transaction_id: number | null;
  amount: string;
  approved_amount?: string | null;
  receipt_amount?: string | null;
  category: string;
  approval_date: string;
  merchant_name?: string | null;
  expense_type?: ExpenseType | null;
  prepaid_receipt_status?: PrepaidReceiptStatus | null;
  reimbursement_method?: ReimbursementMethod;
  /** True while the overspend top-up to the employee is queued or failed. */
  overspend_settlement_pending?: boolean;
  description: string;
  sending_purpose?: string;
  purchase_date: string;
  payroll_date?: string | null;
  country_id: number;
  note: string | null;
  attachments?: Attachment[];
  created_by?: string;
  /** Name of the person who approved or declined the expense, once the API sends it. */
  reviewed_by?: string | null;
  status: ExpenseStatus;
  decline_note: string | null;
  declined_by?: number | null;
  declined_at?: string | null;
  declined_source?: 'manual' | 'command' | null;
  payment_date: string | null;
  rejection_date?: string | null;
  request_date: string;
}

export type ExpenseSortBy = 'id' | 'amount' | 'due_date' | 'created_at' | 'approved_at';

export interface ExpenseSort {
  by: ExpenseSortBy;
  order: 'asc' | 'desc';
}

/**
 * Default list sort aligned with GET /expenses:
 * approved-only filters → approved_at desc; otherwise created_at desc.
 */
export function getDefaultExpenseListSort(status?: ExpenseStatus[]): ExpenseSort {
  const isOnlyApproved = !!status?.length && status.every((value) => value === 'approved');

  if (isOnlyApproved) return { by: 'approved_at', order: 'desc' };

  return { by: 'created_at', order: 'desc' };
}

export interface ExpenseFilters {
  page?: number;
  per_page?: number;
  status?: ExpenseStatus[];
  category?: string;
  expense_type?: ExpenseType;
  prepaid_receipt_status?: PrepaidReceiptStatus[];
  /** Drops prepaid rows in these receipt states; reimbursements (`null`) are never excluded. */
  exclude_prepaid_receipt_status?: PrepaidReceiptStatus[];
  reimbursement_method?: ReimbursementMethod;
  currency_id?: number;
  currency?: string;
  user_id?: number;
  team_member_ids?: number[];
  organisation_ids?: number[];
  request_date_from?: string;
  request_date_to?: string;
  search?: string;
  organisation_id?: number;
  team_id?: number;
  view_self_inputs?: boolean;
  sort?: ExpenseSort;
}

export interface ExpenseResponse {
  data: Expense;
  message: string;
  status: number;
  type: string;
}

export interface ExpensesListResponse {
  data: Expense[];
  pagination: PaginationMeta;
  currency_sum?: number;
  message: string;
  status: number;
  type: string;
}

export interface BulkDeclineRequest {
  organisation_id: number;
  ids: number[];
  decline_note: string;
  team_id?: number;
}

export interface PayExpenseResponse {
  id: number;
  user_id: number;
  status: ExpenseStatus;
  transaction_id: number;
  payment_date: string;
}

export const EXPENSE_SUBMISSION_WINDOW_RULES = ['cutoff', 'current_month'] as const;

export type ExpenseSubmissionWindowRule = (typeof EXPENSE_SUBMISSION_WINDOW_RULES)[number];

export interface ExpenseSubmissionWindow {
  rule: ExpenseSubmissionWindowRule;
  uses_cutoff: boolean;
  purchase_date_min: string | null;
  purchase_date_max: string | null;
}

export const FALLBACK_EXPENSE_SUBMISSION_WINDOW: ExpenseSubmissionWindow = {
  rule: 'cutoff',
  uses_cutoff: true,
  purchase_date_min: null,
  purchase_date_max: null,
};

export const EXPENSE_SUBMISSION_WINDOW_QUERY_KEY = 'expense-submission-window';

export function getExpenseSubmissionWindowQueryKey(
  organisationId: number,
  userId?: number
): readonly [string, number, number | undefined] {
  return [EXPENSE_SUBMISSION_WINDOW_QUERY_KEY, organisationId, userId];
}

export function parseExpenseWindowDate(value: string | null): Date | null {
  if (!value) return null;

  const parsed = parseDateString(value, 'dd/MM/yyyy');
  if (!parsed) return null;

  return normalizeDate(parsed);
}

export function isExpensePurchaseDateDisabled(
  date: Date,
  window?: ExpenseSubmissionWindow | null,
  referenceDate = new Date()
): boolean {
  const day = normalizeDate(date);
  const today = normalizeDate(referenceDate);

  if (day > today) return true;

  const minDate = parseExpenseWindowDate(window?.purchase_date_min ?? null);
  const maxDate = parseExpenseWindowDate(window?.purchase_date_max ?? null);

  if (minDate && day < minDate) return true;
  if (maxDate && day > maxDate) return true;

  return false;
}

export function getExpensePurchaseDateHint(
  window: ExpenseSubmissionWindow | null | undefined
): string | null {
  if (window?.rule === 'current_month') return 'Purchase date must be in the current month.';

  return null;
}

export function isImmediateExpenseReimbursement(reimbursementMethod?: string | null): boolean {
  return reimbursementMethod === 'wallet' || reimbursementMethod === 'card';
}

export function shouldSkipExpenseMonthlyInputCutoffLock(
  window?: ExpenseSubmissionWindow | null,
  reimbursementMethod?: string | null
): boolean {
  if (window?.uses_cutoff === false) return true;

  return isImmediateExpenseReimbursement(reimbursementMethod);
}

export function shouldRestrictExpenseInputToCurrentMonth(
  window?: ExpenseSubmissionWindow | null
): boolean {
  return window?.rule === 'current_month';
}

export function getExpensePurchaseDateFieldError(error: {
  message?: string;
  errors?: Record<string, string[]>;
}): string | null {
  const fieldMessage = error.errors?.purchase_date?.[0];
  if (fieldMessage) return fieldMessage;

  if (error.message && /purchase date/i.test(error.message)) return error.message;

  return null;
}
